import {
  PmsSettings,
  PmsProviderOption,
  CredentialsStatus,
  UpdatePmsCredentialsRequest,
  UpdatePmsSettingsRequest,
  EnablePmsRequest,
  TestConnectionResponse,
  SyncJob,
  SyncEntityType,
  SyncStats,
  RetryStats,
  ReconciliationStatus,
  SyncJobsFilters,
  SyncJobsExportParams,
  PaginatedSyncJobs,
} from "@/types/pms";
import { BaseService } from "./BaseService";

class PmsService extends BaseService {
  private basePath(hotelId: string) {
    return `/hotels/${hotelId}/pms`;
  }

  async getProviders(): Promise<PmsProviderOption[]> {
    return this.get<PmsProviderOption[]>("/pms/providers");
  }

  async getSettings(hotelId: string): Promise<PmsSettings> {
    return this.get<PmsSettings>(`${this.basePath(hotelId)}/settings`);
  }

  async updateSettings(
    hotelId: string,
    data: UpdatePmsSettingsRequest,
  ): Promise<PmsSettings> {
    return this.put<PmsSettings>(`${this.basePath(hotelId)}/settings`, data);
  }

  async getCredentialsStatus(hotelId: string): Promise<CredentialsStatus> {
    return this.get<CredentialsStatus>(`${this.basePath(hotelId)}/credentials`);
  }

  async updateCredentials(
    hotelId: string,
    data: UpdatePmsCredentialsRequest,
  ): Promise<void> {
    return this.put<void>(`${this.basePath(hotelId)}/credentials`, data);
  }

  async deleteCredentials(hotelId: string): Promise<void> {
    return this.delete<void>(`${this.basePath(hotelId)}/credentials`);
  }

  async testConnection(hotelId: string): Promise<TestConnectionResponse> {
    return this.post<TestConnectionResponse>(
      `${this.basePath(hotelId)}/test-connection`,
    );
  }

  async enable(hotelId: string, data: EnablePmsRequest): Promise<PmsSettings> {
    return this.post<PmsSettings>(`${this.basePath(hotelId)}/enable`, data);
  }

  async disable(hotelId: string): Promise<PmsSettings> {
    return this.post<PmsSettings>(`${this.basePath(hotelId)}/disable`);
  }

  async triggerFullSync(hotelId: string): Promise<SyncJob> {
    return this.post<SyncJob>(`${this.basePath(hotelId)}/sync`, undefined, {
      disableToast: true,
    });
  }

  async triggerSelectiveSync(
    hotelId: string,
    entities: SyncEntityType[],
  ): Promise<SyncJob> {
    return this.post<SyncJob>(
      `${this.basePath(hotelId)}/sync`,
      { entities },
      {
        disableToast: true,
      },
    );
  }

  async triggerIncrementalSync(hotelId: string): Promise<SyncJob> {
    return this.post<SyncJob>(
      `${this.basePath(hotelId)}/sync/incremental`,
      undefined,
      { disableToast: true },
    );
  }

  async getSyncJobs(
    hotelId: string,
    filters?: SyncJobsFilters,
  ): Promise<PaginatedSyncJobs> {
    return this.get<PaginatedSyncJobs>(`${this.basePath(hotelId)}/sync/jobs`, {
      page: filters?.page ?? 1,
      pageSize: filters?.pageSize ?? 20,
      ...(filters?.from ? { from: filters.from } : {}),
      ...(filters?.to ? { to: filters.to } : {}),
      ...(filters?.status ? { status: filters.status } : {}),
      ...(filters?.type ? { type: filters.type } : {}),
      ...(filters?.sortBy ? { sortBy: filters.sortBy } : {}),
      ...(filters?.sortDirection
        ? { sortDirection: filters.sortDirection }
        : {}),
    });
  }

  async exportSyncJobs(
    hotelId: string,
    params: SyncJobsExportParams,
  ): Promise<Blob> {
    return this.getBlob(`${this.basePath(hotelId)}/sync/jobs/export`, {
      format: params.format,
      ...(params.from ? { from: params.from } : {}),
      ...(params.to ? { to: params.to } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...(params.type ? { type: params.type } : {}),
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.sortDirection ? { sortDirection: params.sortDirection } : {}),
    });
  }

  async getSyncJob(hotelId: string, jobId: string): Promise<SyncJob> {
    return this.get<SyncJob>(`${this.basePath(hotelId)}/sync/jobs/${jobId}`);
  }

  async cancelSyncJob(hotelId: string, jobId: string): Promise<SyncJob> {
    return this.post<SyncJob>(
      `${this.basePath(hotelId)}/sync/jobs/${jobId}/cancel`,
    );
  }

  async getSyncStats(hotelId: string): Promise<SyncStats> {
    // Fetched on PMS Settings mount, where the hotel may have no PMS
    // configured yet (404) — the pages render their own error/empty states.
    return this.get<SyncStats>(
      `${this.basePath(hotelId)}/stats/sync`,
      undefined,
      {
        disableToast: true,
      },
    );
  }

  async getRetryStats(hotelId: string): Promise<RetryStats> {
    return this.get<RetryStats>(`${this.basePath(hotelId)}/stats/retry`);
  }

  async getReconciliationStatus(
    hotelId: string,
  ): Promise<ReconciliationStatus> {
    return this.get<ReconciliationStatus>(
      `${this.basePath(hotelId)}/stats/reconciliation`,
    );
  }

  async triggerReconciliation(hotelId: string): Promise<void> {
    return this.post<void>(`${this.basePath(hotelId)}/reconciliation/trigger`);
  }
}

export const pmsService = new PmsService();
